import React, { useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

// Lightweight lightbox for the Library detail gallery.
// • Tapping an image opens it at full width.
// • Arrow keys / on-screen arrows step through the gallery.
// • ESC closes.
export default function ImageLightbox({ images, index, onClose, onIndex, alt = '' }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && images.length > 1) onIndex((index + 1) % images.length);
      if (e.key === 'ArrowLeft' && images.length > 1) onIndex((index - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, images.length, onClose, onIndex]);

  if (index < 0 || !images || !images[index]) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-3 sm:p-8"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="lightbox-close"
        className="absolute top-3 right-3 sm:top-6 sm:right-6 w-10 h-10 rounded-full bg-paper/20 hover:bg-paper/40 text-white flex items-center justify-center"
        aria-label="Close"
      >
        <X size={20} />
      </button>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onIndex((index - 1 + images.length) % images.length); }}
            data-testid="lightbox-prev"
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-paper/20 hover:bg-paper/40 text-white flex items-center justify-center"
            aria-label="Previous"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onIndex((index + 1) % images.length); }}
            data-testid="lightbox-next"
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-paper/20 hover:bg-paper/40 text-white flex items-center justify-center"
            aria-label="Next"
          >
            <ChevronRight size={22} />
          </button>
        </>
      )}

      <img
        src={images[index]}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
        className="max-w-full max-h-[88vh] object-contain rounded-2xl"
      />

      {images.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/80 font-medium px-3 py-1.5 rounded-full bg-paper/15">
          {index + 1} / {images.length}
        </div>
      )}
    </div>
  );
}
